import React, { useState, useMemo, useEffect } from "react";
import { FiCalendar } from "react-icons/fi";
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Collapse } from "@mui/material";
import { IoIosArrowDown } from "react-icons/io";
import { useTheme } from "../../../ThemeContext";

const formatMonthLabel = (monthStr) => {
  if (!monthStr) return "—";
  try {
    if (String(monthStr).includes("-")) {
      const parts = String(monthStr).split("-");
      if (parts.length >= 2) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
        return format(d, "MMMM yyyy");
      }
    }
    return monthStr;
  } catch {
    return monthStr;
  }
};

const ContractEarningsView = ({ apiInvoices = [], isGridLoading = false, dataGridSx }) => {
  const { isDarkMode } = useTheme();

  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    if (Array.isArray(apiInvoices) && apiInvoices.length > 0) {
      const initial = {};
      apiInvoices.forEach((inv) => {
        initial[inv.invoice_id] = true;
      });
      setExpandedMonths(initial);
    }
  }, [apiInvoices]);

  const toggleMonth = (invId) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [invId]: !prev[invId],
    }));
  };

  const allExpanded = useMemo(() => {
    if (!apiInvoices.length) return false;
    return apiInvoices.every((inv) => expandedMonths[inv.invoice_id] !== false);
  }, [apiInvoices, expandedMonths]);

  const handleToggleAllMonths = () => {
    const nextState = !allExpanded;
    const updated = {};
    apiInvoices.forEach((inv) => {
      updated[inv.invoice_id] = nextState;
    });
    setExpandedMonths(updated);
  };

  const sortedInvoices = useMemo(() => {
    if (!Array.isArray(apiInvoices)) return [];
    return [...apiInvoices].sort((a, b) => {
      const mA = a.reporting_month || "";
      const mB = b.reporting_month || "";
      if (mA && mB) return mB.localeCompare(mA);
      return (b.invoice_id || 0) - (a.invoice_id || 0);
    });
  }, [apiInvoices]);

  const getInvoiceTotals = (inv) => {
    const resources = Array.isArray(inv.invoice_resources) ? inv.invoice_resources : [];
    const totalHrs = resources.reduce(
      (sum, r) => sum + (parseFloat(r.working_hours || 0) || 0),
      0
    );
    const totalBilled =
      inv.total_earning != null
        ? Number(inv.total_earning)
        : resources.reduce((sum, r) => sum + (Number(r.earning || 0) || 0), 0);
    const totalCost =
      inv.total_cost != null
        ? Number(inv.total_cost)
        : resources.reduce((sum, r) => sum + (Number(r.cost || 0) || 0), 0);
    const totalMargin =
      inv.total_profit != null
        ? Number(inv.total_profit)
        : totalBilled - totalCost;
    const ratio =
      totalCost > 0 ? (totalBilled / totalCost).toFixed(2) + "x" : "—";

    return {
      totalHrs: totalHrs % 1 === 0 ? totalHrs.toFixed(0) : totalHrs.toFixed(2),
      totalBilled: `₹${totalBilled.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      totalCost: `₹${totalCost.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      totalMargin: `₹${totalMargin.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ratio,
      rawRatio: totalCost > 0 ? totalBilled / totalCost : 0,
    };
  };

  const contractColumns = useMemo(
    () => [
      {
        field: "resource",
        headerName: "Resource",
        flex: 1.6,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => {
          const res = params.row;
          return (
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: "28px",
                  height: "28px",
                  backgroundColor: isDarkMode ? "rgba(4, 161, 137, 0.2)" : "#e6f7f3",
                  color: isDarkMode ? "#34d399" : "#047857",
                  fontSize: "11px",
                  flexShrink: 0,
                }}
              >
                {(res.name || res.user_name || "U")
                  .split(" ")
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </div>
              <span className={`fw-medium ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "13px" }}>
                {res.name || res.user_name || "Unknown Resource"}
              </span>
            </div>
          );
        },
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.9,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          const isReimbursement = params.row.billing_type?.toLowerCase() === "reimbursement";
          return (
            <span
              className={`badge ${
                isReimbursement
                  ? isDarkMode
                    ? "bg-secondary bg-opacity-25 text-light border border-secondary"
                    : "bg-secondary-subtle text-secondary border"
                  : isDarkMode
                  ? "bg-info bg-opacity-10 text-info border border-info border-opacity-25"
                  : "bg-info-subtle text-primary border"
              }`}
              style={{ fontSize: "11.5px", fontWeight: 500, padding: "3px 8px" }}
            >
              {params.row.billing_type || "Billable"}
            </span>
          );
        },
      },
      {
        field: "hours",
        headerName: "Hrs",
        flex: 0.8,
        minWidth: 80,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            {Number(params.value || 0) % 1 === 0 ? Number(params.value || 0).toFixed(0) : Number(params.value || 0).toFixed(2)}
          </span>
        ),
      },
      {
        field: "billed",
        headerName: "Billed",
        flex: 1.1,
        minWidth: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            ₹{Number(params.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost",
        flex: 1.0,
        minWidth: 100,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            ₹{Number(params.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        field: "margin",
        headerName: "Margin",
        flex: 1.1,
        minWidth: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            ₹{Number(params.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        field: "ratio",
        headerName: "Ratio",
        flex: 0.9,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const res = params.row;
          const cost = Number(res.cost || 0);
          const billed = Number(res.billed || res.earning || 0);
          const isReimbursement = res.billing_type?.toLowerCase() === "reimbursement";
          if (isReimbursement && cost === 0) {
            return (
              <span className={isDarkMode ? "text-secondary" : "text-muted"} style={{ fontSize: "12px" }}>
                excluded
              </span>
            );
          }
          if (cost === 0) {
            return <span className={isDarkMode ? "text-secondary" : "text-muted"}>—</span>;
          }
          const numRatio = billed / cost;
          const color =
            numRatio >= 1.5
              ? isDarkMode ? "#34d399" : "#047857"
              : numRatio >= 1.0
              ? isDarkMode ? "#38bdf8" : "#0284c7"
              : isDarkMode ? "#f87171" : "#dc2626";

          return (
            <span className="fw-bold" style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color }}>
              {numRatio.toFixed(2)}x
            </span>
          );
        },
      },
    ],
    [isDarkMode]
  );

  return (
    <div className="contract-earnings-wrapper">
      {/* Monthly Content */}
      {isGridLoading ? (
        <div className={`card border rounded shadow-sm p-5 text-center mb-4 ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
          <div className="d-flex flex-column align-items-center justify-content-center py-4">
            <div className="spinner-border mb-3" style={{ color: "#04a189" }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className={isDarkMode ? "text-light" : "text-muted"} style={{ fontSize: "13.5px" }}>
              Loading contract earnings data...
            </span>
          </div>
        </div>
      ) : sortedInvoices.length === 0 ? (
        <>
          <div
            className={`card border rounded shadow-sm p-2 px-3 mb-3 d-flex flex-row align-items-center justify-content-between flex-wrap gap-2 ${
              isDarkMode ? "bg-dark border-secondary" : "bg-white"
            }`}
          >
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center gap-2 fw-semibold"
                style={{
                  fontSize: "13.5px",
                  color: isDarkMode ? "#34d399" : "#04a189",
                  padding: "4px 8px",
                }}
              >
                <FiCalendar size={17} />
                Monthly Invoices Breakdown
              </span>
              <span
                className={`badge ${
                  isDarkMode
                    ? "bg-secondary bg-opacity-25 text-light border border-secondary"
                    : "bg-light text-muted border"
                }`}
                style={{ fontSize: "11.5px", fontWeight: 500 }}
              >
                0 Invoices
              </span>
            </div>
          </div>
          <div className={`card border rounded shadow-sm p-5 text-center mb-4 ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}>
            <div className="py-4">
              <FiCalendar size={38} className={`mb-3 ${isDarkMode ? "text-secondary" : "text-muted"}`} />
              <h6 className={`fw-bold mb-1 ${isDarkMode ? "text-light" : "text-dark"}`}>
                No Contract Invoices Allocated Yet
              </h6>
              <p className={`small mb-0 ${isDarkMode ? "text-secondary" : "text-muted"}`}>
                When contract invoices are mapped and allocated, their monthly resource breakdown will appear here.
              </p>
            </div>
          </div>
        </>
      ) : (
        sortedInvoices.map((inv, idx) => {
          const isExpanded = expandedMonths[inv.invoice_id] !== false;
          const resources = Array.isArray(inv.invoice_resources) ? inv.invoice_resources : [];
          const invTotals = getInvoiceTotals(inv);

          return (
            <div
              key={inv.invoice_id}
              className={`card border rounded shadow-sm ${isDarkMode ? "bg-dark border-secondary" : "bg-white"}`}
              style={{
                overflow: "hidden",
                marginTop: idx > 0 ? "10px" : "0px",
              }}
            >
              {idx === 0 && (
                <div
                  className={`p-2 px-3 d-flex flex-row align-items-center justify-content-between flex-wrap gap-2 border-bottom ${
                    isDarkMode ? "bg-dark border-secondary" : "bg-white"
                  }`}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center gap-2 fw-semibold"
                      style={{
                        fontSize: "13.5px",
                        color: isDarkMode ? "#34d399" : "#04a189",
                        padding: "4px 8px",
                      }}
                    >
                      <FiCalendar size={17} />
                      Monthly Invoices Breakdown
                    </span>
                    <span
                      className={`badge ${
                        isDarkMode
                          ? "bg-secondary bg-opacity-25 text-light border border-secondary"
                          : "bg-light text-muted border"
                      }`}
                      style={{ fontSize: "11.5px", fontWeight: 500 }}
                    >
                      {sortedInvoices.length} {sortedInvoices.length === 1 ? "Invoice" : "Invoices"}
                    </span>
                  </div>

                  {sortedInvoices.length > 0 && (
                    <button
                      type="button"
                      className={`btn btn-sm ${isDarkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
                      style={{ fontSize: "12.5px" }}
                      onClick={handleToggleAllMonths}
                    >
                      {allExpanded ? "Collapse All" : "Expand All"}
                    </button>
                  )}
                </div>
              )}
              {/* Month Header / Accordion trigger */}
              <div
                className={`card-header py-3 d-flex align-items-center justify-content-between flex-wrap gap-2 ${
                  isDarkMode ? "bg-dark border-secondary text-light" : "bg-white"
                } ${isExpanded ? "border-bottom" : ""}`}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => toggleMonth(inv.invoice_id)}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <IoIosArrowDown
                    size={18}
                    style={{
                      color: "#04a189",
                      transition: "transform 0.25s ease-in-out",
                      transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                      flexShrink: 0,
                    }}
                  />
                  <span className={`fw-bold ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "14px" }}>
                    {formatMonthLabel(inv.reporting_month)}
                  </span>
                  <span
                    className={`badge ${
                      isDarkMode
                        ? "bg-secondary bg-opacity-25 text-light border border-secondary"
                        : "bg-light text-dark border"
                    }`}
                    style={{ fontSize: "11.5px" }}
                  >
                    {inv.invoice_number || `INV-${inv.invoice_id}`}
                  </span>
                  {inv.status && (
                    <span
                      className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 text-capitalize"
                      style={{ fontSize: "11px", fontWeight: 600, padding: "3px 8px" }}
                    >
                      {inv.status}
                    </span>
                  )}
                </div>

                {/* Summary metrics chip on right */}
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-1" style={{ fontSize: "12.5px" }}>
                    <span className={isDarkMode ? "text-secondary" : "text-muted"}>Hrs:</span>
                    <span
                      className={`fw-bold ${isDarkMode ? "text-light" : "text-dark"}`}
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {invTotals.totalHrs}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1" style={{ fontSize: "12.5px" }}>
                    <span className={isDarkMode ? "text-secondary" : "text-muted"}>Billed:</span>
                    <span
                      className="fw-bold text-success"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {invTotals.totalBilled}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1" style={{ fontSize: "12.5px" }}>
                    <span className={isDarkMode ? "text-secondary" : "text-muted"}>Cost:</span>
                    <span
                      className={`fw-bold ${isDarkMode ? "text-light" : "text-dark"}`}
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {invTotals.totalCost}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1" style={{ fontSize: "12.5px" }}>
                    <span className={isDarkMode ? "text-secondary" : "text-muted"}>Margin:</span>
                    <span
                      className="fw-bold"
                      style={{
                        color: isDarkMode ? "#60a5fa" : "#2563eb",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {invTotals.totalMargin}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1" style={{ fontSize: "12.5px" }}>
                    <span className={isDarkMode ? "text-secondary" : "text-muted"}>Ratio:</span>
                    <span
                      className="fw-bold"
                      style={{
                        color:
                          invTotals.rawRatio >= 1.5
                            ? isDarkMode ? "#34d399" : "#047857"
                            : invTotals.rawRatio >= 1.0
                            ? isDarkMode ? "#38bdf8" : "#0284c7"
                            : isDarkMode ? "#f87171" : "#dc2626",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {invTotals.ratio}
                    </span>
                  </div>
                </div>
              </div>

              {/* Collapsible Table Section - MUI DataGrid */}
              <Collapse in={isExpanded} timeout={250}>
                <Box sx={{ width: "100%", backgroundColor: isDarkMode ? "#1e1e1e" : "#fff" }}>
                  <DataGrid
                    rows={resources.map((r, idx) => ({
                      id: r.invoice_resource_id || r.user_id || `${inv.invoice_id}-res-${idx}`,
                      resource: r.name || r.user_name || "Unknown Resource",
                      billing_type: r.billing_type || "Billable",
                      hours: parseFloat(r.working_hours || 0),
                      billed: Number(r.earning || 0),
                      cost: Number(r.cost || 0),
                      margin:
                        r.profit_margin != null
                          ? Number(r.profit_margin)
                          : Number(r.earning || 0) - Number(r.cost || 0),
                      ...r,
                    }))}
                    columns={contractColumns}
                    autoHeight
                    hideFooterPagination
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    rowHeight={48}
                    slots={{
                      footer: () => {
                        if (resources.length === 0) return null;
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e0e0e0",
                              backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
                              minHeight: "48px",
                              width: "100%",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: isDarkMode ? "#f1f5f9" : "#1e293b",
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1.6,
                                minWidth: 200,
                                px: "10px",
                                display: "flex",
                                alignItems: "center",
                                boxSizing: "border-box",
                              }}
                            >
                              Total
                            </Box>
                            <Box
                              sx={{
                                flex: 0.9,
                                minWidth: 100,
                                px: "10px",
                                boxSizing: "border-box",
                              }}
                            />
                            <Box
                              sx={{
                                flex: 0.8,
                                minWidth: 80,
                                textAlign: "right",
                                px: "10px",
                                boxSizing: "border-box",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {invTotals.totalHrs}
                            </Box>
                            <Box
                              sx={{
                                flex: 1.1,
                                minWidth: 110,
                                textAlign: "right",
                                px: "10px",
                                boxSizing: "border-box",
                                color: isDarkMode ? "#34d399" : "#047857",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {invTotals.totalBilled}
                            </Box>
                            <Box
                              sx={{
                                flex: 1.0,
                                minWidth: 100,
                                textAlign: "right",
                                px: "10px",
                                boxSizing: "border-box",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {invTotals.totalCost}
                            </Box>
                            <Box
                              sx={{
                                flex: 1.1,
                                minWidth: 110,
                                textAlign: "right",
                                px: "10px",
                                boxSizing: "border-box",
                                color: isDarkMode ? "#60a5fa" : "#2563eb",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {invTotals.totalMargin}
                            </Box>
                            <Box
                              sx={{
                                flex: 0.9,
                                minWidth: 90,
                                textAlign: "right",
                                px: "10px",
                                boxSizing: "border-box",
                                fontVariantNumeric: "tabular-nums",
                                color:
                                  invTotals.rawRatio >= 1.5
                                    ? isDarkMode ? "#34d399" : "#047857"
                                    : invTotals.rawRatio >= 1.0
                                    ? isDarkMode ? "#38bdf8" : "#0284c7"
                                    : isDarkMode ? "#f87171" : "#dc2626",
                              }}
                            >
                              {invTotals.ratio}
                            </Box>
                          </Box>
                        );
                      },
                    }}
                    sx={dataGridSx}
                  />
                </Box>
              </Collapse>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ContractEarningsView;
